import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  UploadCloud,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  Hash,
  KeyRound,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const SEARCH_TYPES = {
  nota: {
    label: 'Buscar por nota',
    placeholder: 'Digite o número da nota fiscal',
    icon: Hash,
  },
  chave: {
    label: 'Buscar por chave',
    placeholder: 'Digite a chave de acesso com 44 dígitos',
    icon: KeyRound,
  },
};

const formatEmissionDate = (value) => {
  if (!value) return '-';
  const [year, month, day] = String(value).split('-');
  if (year && month && day) return `${day}/${month}/${year}`;
  return value;
};

export default function UploadXML() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [searchType, setSearchType] = useState('nota');
  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);

  const resetResult = () => setUploadResult(null);
  const isExistingSearchResult = uploadResult?.success && uploadResult?.data?.import_status === 'existing';
  const isAvailableForConference = uploadResult?.data?.available_for_conference !== false;
  const resultTone = uploadResult?.success
    ? isExistingSearchResult
      ? {
          container: 'bg-amber-50 border-amber-200',
          icon: 'text-amber-600',
          title: 'text-amber-800',
          text: 'text-amber-700',
          button: 'bg-amber-600 hover:bg-amber-700',
        }
      : {
          container: 'bg-emerald-50 border-emerald-200',
          icon: 'text-emerald-600',
          title: 'text-emerald-800',
          text: 'text-emerald-700',
          button: 'bg-emerald-600 hover:bg-emerald-700',
        }
    : {
        container: 'bg-red-50 border-red-200',
        icon: 'text-red-600',
        title: 'text-red-800',
        text: 'text-red-700',
        button: '',
      };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xml')) {
        setFile(droppedFile);
        resetResult();
      } else {
        toast.error('Por favor, selecione um arquivo XML');
      }
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      resetResult();
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/invoices/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult({ success: true, data: response.data });
      toast.success('NF-e importada com sucesso!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao importar XML';
      setUploadResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleSearchImport = async (e) => {
    e.preventDefault();
    const trimmedValue = searchValue.trim();
    if (!trimmedValue) return;

    setSearching(true);
    resetResult();

    try {
      const params = new URLSearchParams({ [searchType]: trimmedValue });
      const response = await api.get(`/invoices/import?${params.toString()}`);
      setUploadResult({ success: true, data: response.data });
      toast.success(
        response.data?.import_status === 'existing'
          ? 'NF-e já cadastrada no sistema'
          : 'NF-e carregada com sucesso!'
      );
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao buscar nota';
      setUploadResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setSearching(false);
    }
  };

  const handleStartConference = () => {
    if (uploadResult?.data) {
      navigate('/conference', { state: { invoice: uploadResult.data } });
    }
  };

  const selectedSearchType = SEARCH_TYPES[searchType];
  const SearchIcon = selectedSearchType.icon;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="upload-title">
            Upload XML / Busca por nota ou chave
          </h1>
          <p className="text-slate-500 mt-1">
            Importe por XML, número da nota ou chave de acesso
          </p>
        </div>

        <div className="grid gap-8 xl:grid-cols-[minmax(0,2fr)_minmax(360px,1fr)]">
          <div className="space-y-8">
            <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="mb-5">
                <h2 className="text-xl font-heading font-semibold text-slate-800">
                  Buscar nota sem XML
                </h2>
                <p className="text-slate-500 mt-1">
                  Consulte os itens pela API externa e carregue a NF-e direto para conferência.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mb-5">
                {Object.entries(SEARCH_TYPES).map(([key, option]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSearchType(key)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      searchType === key
                        ? 'border-[#1a365d] bg-[#1a365d] text-white'
                        : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSearchImport} className="flex flex-col gap-4 lg:flex-row">
                <div className="flex-1">
                  <Label htmlFor="invoice-search" className="mb-2 block">
                    {selectedSearchType.label}
                  </Label>
                  <div className="relative">
                    <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <Input
                      id="invoice-search"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      placeholder={selectedSearchType.placeholder}
                      className="h-12 pl-11 font-mono"
                      data-testid="invoice-query-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={searching}
                  className="h-12 px-6 bg-[#1a365d] hover:bg-[#1a365d]/90 self-end"
                  data-testid="invoice-query-button"
                >
                  {searching ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="mr-2" size={18} />
                      Buscar
                    </>
                  )}
                </Button>
              </form>
            </section>

            <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="mb-5">
                <h2 className="text-xl font-heading font-semibold text-slate-800">
                  Upload de XML
                </h2>
                <p className="text-slate-500 mt-1">
                  Importe o arquivo XML quando ele estiver disponível.
                </p>
              </div>

              <div
                className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                  dragActive
                    ? 'border-[#1a365d] bg-[#1a365d]/5'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".xml"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-input"
                />

                <div className="flex flex-col items-center gap-4">
                  <div className={`p-4 rounded-full ${dragActive ? 'bg-[#1a365d]/10' : 'bg-slate-100'}`}>
                    <UploadCloud
                      size={40}
                      className={dragActive ? 'text-[#1a365d]' : 'text-slate-400'}
                    />
                  </div>

                  <div>
                    <p className="text-lg font-medium text-slate-700">
                      Arraste e solte o arquivo XML aqui
                    </p>
                    <p className="text-slate-500 mt-1">
                      ou clique para selecionar
                    </p>
                  </div>
                </div>
              </div>

              {file && !uploadResult && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-between gap-4 animate-fade-in">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="text-[#1a365d] flex-shrink-0" size={24} />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                    data-testid="upload-button"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <UploadCloud className="mr-2" size={18} />
                        Importar
                      </>
                    )}
                  </Button>
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-6">
            {uploadResult && (
              <div className={`p-6 rounded-xl border animate-fade-in ${resultTone.container}`}>
                <div className="flex items-start gap-4">
                  {uploadResult.success ? (
                    <CheckCircle2 className={`${resultTone.icon} flex-shrink-0`} size={24} />
                  ) : (
                    <XCircle className="text-red-600 flex-shrink-0" size={24} />
                  )}

                  <div className="flex-1">
                    <h3 className={`font-semibold ${resultTone.title}`}>
                      {uploadResult.success
                        ? isExistingSearchResult
                          ? 'NF-e já cadastrada'
                          : 'NF-e pronta para conferência'
                        : 'Falha ao carregar NF-e'}
                    </h3>

                    {uploadResult.success ? (
                      <div className="mt-3 space-y-2">
                        {isExistingSearchResult && (
                          <p className={resultTone.text}>
                            Essa NF-e já está no sistema
                            {isAvailableForConference
                              ? ' e continua disponível para conferência.'
                              : ' e não está mais disponível para conferência.'}
                          </p>
                        )}
                        <p className={resultTone.text}>
                          <span className="font-medium">Número:</span>{' '}
                          <span className="font-mono">{uploadResult.data.invoice_number}</span>
                        </p>
                        <p className={resultTone.text}>
                          <span className="font-medium">Chave:</span>{' '}
                          <span className="font-mono text-sm break-all">{uploadResult.data.invoice_key}</span>
                        </p>
                        <p className={resultTone.text}>
                          <span className="font-medium">Emissão:</span>{' '}
                          {formatEmissionDate(uploadResult.data.emission_date)}
                        </p>
                        <p className={resultTone.text}>
                          <span className="font-medium">Produtos:</span>{' '}
                          {uploadResult.data.products?.length || 0} itens
                        </p>

                        <Button
                          onClick={handleStartConference}
                          disabled={!isAvailableForConference}
                          className={`mt-4 ${resultTone.button}`}
                          data-testid="start-conference-after-upload"
                        >
                          {isAvailableForConference ? 'Iniciar Conferência' : 'Conferência indisponível'}
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </div>
                    ) : (
                      <p className="mt-2 text-red-700">{uploadResult.error}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold text-slate-800 mb-3">
                Regras da importação
              </h3>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-[#1a365d] font-bold">1.</span>
                  A busca por nota usa o campo `nota` e a busca por chave usa o campo `chave`.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1a365d] font-bold">2.</span>
                  O EAN vem de `codi_bar`, o código interno de `codi_psv` e a descrição de `desc_psv`.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1a365d] font-bold">3.</span>
                  A quantidade esperada usa `qtde_ino` e a emissão mostrada no sistema usa `demi_not`.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1a365d] font-bold">4.</span>
                  Itens sem EAN válido continuam conferíveis pelo código interno.
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
